/* eslint-disable max-len */
/* eslint-disable import/prefer-default-export */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import classNames from 'classnames/bind';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import type {
  FollowUpPrescriptionQuestion as FollowUpPrescriptionQuestionType,
  FollowUpPrescriptionRequest,
  PreparationListItem as PreparationListItemType,
  PreparationQuestion as PreparationQuestionType,
} from '../../../types';
import { Button } from '../../../components/atoms';
import { Dialog } from '../../../components/atoms/Dialog/Dialog';
import style from './FollowRecipe.module.scss';
import { followupPrescriptionRequest } from '../../../utils/apiCalls';
import { setChosenAvailablePrescription, setPatientInfo } from '../patientOverviewSlice';
import { Checkbox } from '../../../components/atoms/Checkbox/Checkbox';
import { updatePatient } from '../../../utils/apiCalls';
import { Icon } from '../../../components/atoms/Icon/Icon';
import Tooltip from '../../../components/atoms/Tooltip/Tooltip';

const cx = classNames.bind(style);

type PropsTypes = {
  currentMedication: PreparationListItemType[];
  getPatient: () => void;
  patientInfo: any;
};

export function FollowRecipe({ currentMedication, getPatient, patientInfo }: PropsTypes): JSX.Element {
  const pageClasses = cx({ Recipe: true });
  const GreenHeading = cx('GreenHeading');
  const TextBold = cx('TextBold');
  const TextLabel = cx('TextLabel');
  const ColumnContainer = cx('ColumnContainer');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const followUpPrescriptionQuestion = patientInfo?.followUpPrescriptionQuestion;
  const followUpPrescriptionRequest = patientInfo?.followUpPrescriptionRequest;
  const currentDays = patientInfo?.prescriptionWindowDays;

  const [doctorsFreeText, setDoctorsFreeText] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [followupRequestToShow, setFollowupRequestToShow] = useState<FollowUpPrescriptionRequest | undefined>();
  const [followupQuestionToShow, setFollowupQuestionToShow] = useState<FollowUpPrescriptionQuestionType | undefined>();
  const [preparationQuestion, setPreparationQuestion] = useState<PreparationQuestionType[] | undefined>();
  const [disabled, setDisabled] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<string[]>([]);
  const [inputDays, setInputDays] = useState(currentDays);
  const [saving, setSaving] = useState(false);
  const [availableFollowUpPrescription, setAvailableFollowUpPrescription] = useState<number>(0);
  const [isEditable, setIsEditable] = useState(false);
  const dispatch = useDispatch();

  const additinalInfoItems = [{ key: 'erx', label: 'e-Rezept ausgestellt' }];

  const trafficMapping = [
    { id: undefined, value: '' },
    { id: 1, value: 'Ja' },
    { id: 2, value: 'Nein' },
    { id: 3, value: 'Ich fahre kein Auto' },
  ];

  const closeApproveDialog = () => setShowApproveDialog(false);
  const closeDeclineDialog = () => setShowDeclineDialog(false);

  const onAdditionalInfoChange = (selectedKeys: string[]): void => {
    setAdditionalInfo(selectedKeys);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputDays(parseInt(event.target.value));
  };

  const handleSave = async () => {
    if (inputDays !== currentDays) {
      dispatch(setPatientInfo({ ...patientInfo, prescriptionWindowDays: inputDays }));
      const updatedPatientInfo = { prescriptionWindowDays: inputDays };
      setSaving(true);
      try {
        await updatePatient(patientInfo.id, updatedPatientInfo);
      } catch (err) {
        console.log(err);
      } finally {
        setSaving(false);
        setIsEditable(false);
      }
    }
  };

  const decline = async () => {
    setDisabled(true);
    const data = {
      status: 'declined',
      doctorsMessage: doctorsFreeText,
      additionalInfo,
    };
    const declineRequest = await followupPrescriptionRequest(Number(followupRequestToShow?.id), data);

    if (declineRequest.error) {
      toast.error('Etwas ist schiefgelaufen');
    }

    if (declineRequest.data) {
      const newRequest = _.cloneDeep(followUpPrescriptionRequest);
      newRequest[newRequest.length - 1].status = 3;
      newRequest[newRequest.length - 1].doctorId = localStorage.getItem('ninoxId');

      setAvailableFollowUpPrescription(0);
      const newPatientInfo = {
        ...patientInfo,
        availableFollowUpPrescription: 0,
        followUpPrescriptionRequest: newRequest,
      };
      dispatch(setPatientInfo(newPatientInfo));
      dispatch(setChosenAvailablePrescription(0));

      getPatient();
      setFollowupRequestToShow(undefined);

      toast.success('Folgerezept abgelehnt ');
    }

    closeDeclineDialog();
    setDisabled(false);
  };

  const approve = async () => {
    setDisabled(true);
    const data = {
      status: 'accepted',
      doctorsMessage: doctorsFreeText,
      additionalInfo,
    };

    const approveRequest = await followupPrescriptionRequest(Number(followupRequestToShow?.id), data);

    if (approveRequest.error) {
      toast.error('Etwas ist schiefgelaufen');
    }

    if (approveRequest.data) {
      const newRequest = _.cloneDeep(followUpPrescriptionRequest);
      newRequest[newRequest.length - 1].status = 2;
      newRequest[newRequest.length - 1].doctorId = localStorage.getItem('ninoxId');

      setAvailableFollowUpPrescription(availableFollowUpPrescription - 1);
      const newPatientInfo = {
        ...patientInfo,
        availableFollowUpPrescription: availableFollowUpPrescription - 1,
        followUpPrescriptionRequest: newRequest,
      };
      dispatch(setPatientInfo(newPatientInfo));
      dispatch(setChosenAvailablePrescription(availableFollowUpPrescription - 1));

      getPatient();
      setFollowupRequestToShow(undefined);

      toast.success('Folgerezept genehmigt');
    }

    closeApproveDialog();
    setDisabled(false);
  };

  useEffect(() => {
    if (patientInfo.availableFollowUpPrescription !== undefined) {
      dispatch(setChosenAvailablePrescription(patientInfo.availableFollowUpPrescription));
      setAvailableFollowUpPrescription(patientInfo.availableFollowUpPrescription);
    }
  }, []);

  useEffect(() => {
    if (followUpPrescriptionRequest && followUpPrescriptionRequest.length > 0) {
      // Filter to only show FUPs with status 1 or undefined on this screen.
      const lastRequest = followUpPrescriptionRequest.find(
        (request: FollowUpPrescriptionRequest) => !request.status || request.status === 1
      );
      console.log('lastRequest', lastRequest);
      if (lastRequest !== undefined) {
        setFollowupRequestToShow(lastRequest);
        if (followUpPrescriptionQuestion.length > 0) {
          const lastquestion = followUpPrescriptionQuestion.find((question: any) =>
            question.followUpPrescriptionRequest.find((x: number) => x === lastRequest.id)
          );
          setFollowupQuestionToShow(lastquestion);
          setPreparationQuestion(lastquestion?.preparationQuestion);
        }
      }
    }
  }, [followUpPrescriptionRequest]);

  const generalSideEffectsLabels = (item: string, other: string | undefined) => {
    const germanText = item
      .replace('reactions', 'Allergische Reaktionen / Unverträglichkeit')
      .replace('dryMouth', 'Trockene Schleimhäute (trockener Mund / gerötete Augen)')
      .replace('appetite', 'Gesteigerter Appetit (als unangenehmer Effekt)')
      .replace('nausea', 'Übelkeit')
      .replace('increasedFatigue', 'Erhöhte Müdigkeit (als unangenehmer Effekt)')
      .replace('cardioVascularProblems', 'Herzkreislaufprobleme (Blutdruckabfall, Herzrasen, Schwindel, Unwohlsein)')
      .replace('motorLimitations', 'Motorische Einschränkungen');

    const array = germanText.split(',');
    const newArray = array.filter((element) => element !== 'sonstiges');
    if (other) {
      newArray.push(other);
    }

    const finalText = newArray.join(', ');

    return finalText;
  };

  const psychologicalSideEffectsLabels = (item: string, other: string | undefined) => {
    const germanText = item
      .replace('alteredPerception', 'Veränderte Wahrnehmung / Bewusstseinsveränderung')
      .replace('thoughtDisorders', 'Denkstörungen / Gedächtnisstörungen')
      .replace('anxiety', 'Angst und Paniksituationen')
      .replace('hallucinations', 'Halluzinationen / Psychosen')
      .replace('toleranceDevelopment', 'Starke Toleranzentwicklung');
    const array = germanText.split(',');
    const newArray = array.filter((element) => element !== 'sonstiges');
    if (other) {
      newArray.push(other);
    }
    const finalText = array.join(', ');

    return finalText;
  };

  const renderCurrentMedication = () =>
    currentMedication &&
    currentMedication.map((element: PreparationListItemType) => (
      <div className={style.RowContainer} key={element.id}>
        <p className={style.MedicineText}>{element.name}</p>
        {element.dosageUnit === 'g' && (
          <div className={style.MedicineValue}>
            <p className={TextLabel}>{`${element.quantity} ${element.dosageUnit}`}</p>
          </div>
        )}
      </div>
    ));

  const renderExchangePreparationsItems = () => {
    const preparationsToChange = followupQuestionToShow?.preparationQuestion?.filter(
      (question: PreparationQuestionType) => question.changeThisPreparation
    );

    const activeMedication = patientInfo?.medication?.find(
      (medication: any) => followupQuestionToShow?.medicationId === medication?.id
    );

    const exchangeRequestElements = preparationsToChange?.map((question: PreparationQuestionType) => {
      const preparationId = question?.preparationListItemId;

      const originalPreparation = activeMedication?.preparationListItem?.find(
        (preparation: any) => preparation.id === preparationId
      );

      return (
        <p
          className={TextBold}
        >{`Für ${originalPreparation?.name} ${originalPreparation?.cultivar} wünscht sich der Patient ${question?.exchangeWithThisPreparation}`}</p>
      );
    });

    return exchangeRequestElements;
  };

  return (
    <div className={pageClasses}>
      {showApproveDialog && (
        <Dialog
          message={
            'Wollen Sie den Folgerezeptantrag wirklich genehmigen? Der Folgerezeptantrag schließt sich im Anschluss und kann nicht mehr verändert werden.'
          }
          type='positiveDialog'
          spinner={'primary'}
          buttonText='Genehmigen'
          onConfirm={approve}
          onCancel={closeApproveDialog}
          disabled={disabled}
        />
      )}
      {showDeclineDialog && (
        <Dialog
          message={
            'Wollen Sie den Folgerezeptantrag wirklich ablehnen? Der Folgerezeptantrag schließt sich im Anschluss und kann nicht mehr verändert werden.'
          }
          type='negativeDialog'
          buttonText='Ablehnen'
          onConfirm={decline}
          onCancel={closeDeclineDialog}
          disabled={disabled}
        />
      )}
      <div className={style.MainContainer}>
        <div className={ColumnContainer}>
          <h1 className={GreenHeading}>ANTRAG</h1>
          {followupRequestToShow ? (
            <>
              <div className={style.Container}>
                <div className={style.MedicineContainer}>
                  <p className={TextLabel}>Antrag eingereicht am</p>
                  <p className={TextBold}>
                    {followUpPrescriptionRequest &&
                      new Date(
                        followUpPrescriptionRequest[followUpPrescriptionRequest.length - 1].requestDate
                      ).toLocaleDateString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    {' Uhr'}
                  </p>
                </div>
                <div>
                  <div className={style.MedicineContainer}>
                    <p className={TextLabel}>Allgemeine Nebenwirkungen</p>
                    <p className={TextBold}>
                      {followupQuestionToShow?.generalSideEffects &&
                        generalSideEffectsLabels(
                          followupQuestionToShow.generalSideEffects,
                          followupQuestionToShow.generalSideEffectsOther
                        )}
                    </p>
                  </div>
                  <div className={style.MedicineContainer}>
                    <p className={TextLabel}>Psychische Nebenwirkungen</p>
                    <p className={TextBold}>
                      {followupQuestionToShow?.psychologicalSideEffects &&
                        psychologicalSideEffectsLabels(
                          followupQuestionToShow.psychologicalSideEffects,
                          followupQuestionToShow.psychologicalSideEffectsOther
                        )}
                    </p>
                  </div>
                  <div className={style.MedicineContainer}>
                    <p className={TextLabel}>Sicher in der Anwendung?</p>
                    <p className={TextBold}>
                      {followupQuestionToShow?.isSafeHandling === true && 'Ja'}
                      {followupQuestionToShow?.isSafeHandling === false && 'Nein'}
                    </p>
                  </div>
                  <div className={style.MedicineContainer}>
                    <p className={TextLabel}>Fähig am Straßenverkehr teilzunehmen?</p>
                    <p className={TextBold}>
                      {trafficMapping.find((traf) => traf.id === followupQuestionToShow?.roadTraffic)?.value}
                    </p>
                  </div>
                </div>
              </div>
              <div className={style.WhiteBoxContainer}>
                <div className={style.MedicineContainer}>
                  <p className={style.BlackHeadings}>Neue Medikamente</p>
                  <p className={TextBold}>{followupQuestionToShow?.newMedicines || 'Nein'}</p>
                </div>
                <div className={style.MedicineContainer}>
                  <p className={style.BlackHeadings}>Medizinische Neuigkeiten</p>
                  <p className={TextBold}>{followupQuestionToShow?.medicalNews || 'Nein'}</p>
                </div>
                <div className={style.MedicineContainer}>
                  <p className={TextLabel}>Apotheke</p>
                  <p className={TextBold}>{patientInfo?.patientPharmacyRelation?.name}</p>
                </div>
                <div>
                  <h2 className={style.BlackHeadings}>Austauschpräparate</h2>
                  {followupQuestionToShow?.medicationAvailable === 'yes' && (
                    <p className={TextBold}>Lieferbarkeit ist überprüft, keine Austauschpräparate gewünscht.</p>
                  )}
                  {followupQuestionToShow?.medicationAvailable === 'no' && (
                    <p className={TextBold}>
                      Lieferbarkeit ist <b>nicht</b> überprüft, keine Austauschpräparate gewünscht.
                    </p>
                  )}
                  {followupQuestionToShow?.medicationAvailable === 'changeMedication' &&
                    renderExchangePreparationsItems()}
                </div>
                <div>
                  <p className={TextLabel}>
                    Ihre Nachricht an den / die Patient*in (Die Nachricht wird Teil einer automatisierten E-Mail. Füllen
                    Sie diese bitte nur bei Genehmigung aus. Eine Anrede und Verabschiedung werden ebenfalls
                    automatisiert hinzugefügt).
                  </p>
                  <textarea
                    className={style.InputFreeText}
                    value={doctorsFreeText}
                    onChange={(event) => {
                      setDoctorsFreeText(event.target.value);
                    }}
                  ></textarea>
                </div>
                <div>
                  <div className={style.GreyText}>
                    <p>Genehmigen Sie den Antrag erst, nachdem die Verschreibung erstellt wurde.</p>
                  </div>
                  <div className={style.ButtonsContainer}>
                    <Checkbox
                      value={additionalInfo}
                      name='supportInfo'
                      items={additinalInfoItems}
                      layout='horizontal'
                      onChange={onAdditionalInfoChange}
                    />
                    <span style={{ width: '2rem' }}></span>
                    <Button
                      children={'Antrag ablehnen'}
                      className={style.RedButton}
                      type={'secondary'}
                      onClick={() => setShowDeclineDialog(true)}
                    />
                    <Button
                      children={'Antrag genehmigen'}
                      className={style.GreenButton}
                      type={'primary'}
                      onClick={() => setShowApproveDialog(true)}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={style.Container}>
              <p className={TextLabel}>Es liegt kein Antrag vor.</p>
            </div>
          )}
          <h1 className={GreenHeading}>Konsultationszeitraum</h1>
          <div className={style.FirstContainer}>
            <div
              className={style.GeneraInfoContainer}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '2rem',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2rem' }}>
                <Tooltip
                  content='Der Zeitraum, nach dessen Ablauf der Patient ein Videogespräch benötigt.'
                  placement='right'
                >
                  <p className={TextLabel} style={{ display: 'flex', alignItems: 'center' }}>
                    Konsultationszeitraum:
                  </p>
                </Tooltip>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isEditable && (
                    <input
                      type='number'
                      value={inputDays}
                      onChange={handleInputChange}
                      name='prescriptionWindowDays'
                      className={style.SmallInputClass}
                    />
                  )}

                  <p style={{ marginBottom: '15px', fontSize: '1.5rem' }}>
                    {!isEditable && <span>{inputDays} </span>}Tage
                  </p>
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <button
                  onClick={() => setIsEditable(true)}
                  style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <Icon icon='edit' size={25} />
                </button>
              </div>
            </div>
          </div>
          <Button
            onClick={handleSave}
            style={{ marginBottom: '15px' }}
            type='primary'
            disabled={inputDays === currentDays}
            spinner={saving ? 'primary' : 'none'}
          >
            Speichern
          </Button>
        </div>
        {currentMedication.length !== 0 && (
          <div>
            <h1 className={GreenHeading}>AKTUELLE MEDIKATION</h1>
            <div className={style.MedicationContainer}>{renderCurrentMedication()}</div>
          </div>
        )}
      </div>
    </div>
  );
}
